import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { t } from '@reos/i18n';
import type { AnswerTarget } from './AnswerQuestionPanel.js';
import { KIND_CONFIRMATION_NOTE, type CockpitTask } from '../lib/case-cockpit.js';
import { recordBrokerClaim } from '../lib/claims-api.js';
import type { CaseDetail } from '../lib/dd-api.js';
import type { CaseTabId } from '../lib/texts.js';

const cs = { locale: 'cs' } as const;

/** Stitky dopadu ukolu — v predloze „Blokující / Důležité / Hotovo". */
const IMPACT_CAPTIONS: Readonly<Record<CockpitTask['impact'], string>> = {
  high: t('webAdmin.caseCockpit.tasks.impactHigh', cs),
  medium: t('webAdmin.caseCockpit.tasks.impactMedium', cs),
  low: t('webAdmin.caseCockpit.tasks.impactLow', cs),
};

/** Trida stitku a tecky podle dopadu (predloha `badge--bad/--wait`, `dot--bad/--wait`). */
const IMPACT_BADGE_CLASS: Readonly<Record<CockpitTask['impact'], string>> = {
  high: 'rh-a2-badge--bad',
  medium: 'rh-a2-badge--wait',
  low: '',
};
const IMPACT_DOT_CLASS: Readonly<Record<CockpitTask['impact'], string>> = {
  high: 'rh-a2-dot--bad',
  medium: 'rh-a2-dot--wait',
  low: '',
};

/** Karta jedne otazky kokpitu (predloha `.q`): nadpis, stitek, telo. */
function Question({
  heading,
  badge,
  badgeClass,
  hint,
  testId,
  children,
}: {
  readonly heading: string;
  readonly badge?: string;
  readonly badgeClass?: string;
  readonly hint?: string;
  readonly testId: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="rh-a2-q" data-testid={testId}>
      <div className="rh-a2-q__head">
        <h2>{heading}</h2>
        {badge !== undefined ? (
          <span className={`rh-a2-badge ${badgeClass ?? ''}`} data-testid={`${testId}-badge`}>
            {badge}
          </span>
        ) : null}
      </div>
      <div className="rh-a2-q__body">
        {hint !== undefined ? <p className="rh-a2-q__hint">{hint}</p> : null}
        {children}
      </div>
    </section>
  );
}

/**
 * Krok UX2/UXR2 — ZÁLOŽKA KOKPIT: tři otázky makléře podle SCHVÁLENÉHO
 * mockupu `03-detail-zakazky.html`.
 *
 * 1. „Je to v pořádku?“ — semafor a seznam věcí s jednou lidskou větou,
 *    zdrojem a datem; podrobnosti vedou odkazem „Podrobnosti ověření",
 * 2. „Co musím udělat?“ — úkoly s průběhem a štítky Blokující / Důležité /
 *    Hotovo, každý s jedním tlačítkem,
 * Krok UXR3: třetí otázka („Co zbývá?“ — cesta zakázky) se z kokpitu
 * PŘESUNULA na záložku **Historie**. Cestu zakázky dnes nahoře nese pruh
 * pěti fází (`components/case-phase-strip.tsx`, předloha UX v3) a mít ji na
 * téže obrazovce dvakrát je přesně to „všechno splývá dohromady“, na které
 * si R. B. 2. 10. 2026 stěžoval. Karta samotná se nemění — vykresluje ji
 * záložka Historie (`CaseRemainingCard` níž, pořád exportovaná).
 *
 * ROZHODNUTÍ UXR2-4: čtvrtá otázka z kroku UX2 („Co dám klientovi?“ — náhled
 * reportu, schválení, odeslání) se přesunula na záložku **Dokumenty**, kde už
 * stojí objednávky exportů. Předloha má na kokpitu tři karty a report JE
 * dokument; funkce nikam nezmizela (viz `ClientReportCard`).
 */
export function CockpitTab({
  detail,
  tasks,
  onOpenAudit,
  onAnswer,
  onChanged,
  onTasksReload,
}: {
  readonly detail: CaseDetail;
  /**
   * Ukoly makléře. Pocita je STRANKA, ne tahle karta: tentyz seznam potrebuje
   * hlavicka pro „Další krok: …" a dva vypocty ze dvou mist by se rozesly.
   */
  readonly tasks: readonly CockpitTask[];
  readonly onOpenAudit: (tab: CaseTabId, anchor: string | null) => void;
  readonly onAnswer: (target: AnswerTarget) => void;
  /** Detail po potvrzeni typu se nacte znovu (zadani U1 — bez lokalniho prekryvu). */
  readonly onChanged: () => void;
  /** Po akci se znovu nactou lidske ukoly (drzi je stranka). */
  readonly onTasksReload: () => void;
}): React.JSX.Element {
  const ddCaseId = detail.fixture.caseInfo.id;

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const doneCount = tasks.filter((task) => task.done).length;

  /**
   * Jedna akce z karty. `reload` rika, jestli se ma detail nacist znovu —
   * pouze tam, kde se opravdu zmeni (zapsani potvrzeni typu se projevi
   * az v podkladech na vstupu auditu).
   */
  const run = (action: () => Promise<unknown>, ok: string | null, reload: boolean): void => {
    setBusy(true);
    setError(null);
    setNotice(null);
    void action()
      .then(() => {
        if (ok !== null) setNotice(ok);
        onTasksReload();
        if (reload) onChanged();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => { setBusy(false); });
  };

  const confirmKind = (): void => {
    run(
      () => recordBrokerClaim(ddCaseId, { kind: 'FREE_TEXT', text: KIND_CONFIRMATION_NOTE }),
      null,
      true,
    );
  };

  const openTask = (task: CockpitTask): void => {
    const action = task.action;
    switch (action.kind) {
      case 'answer': {
        const question = detail.fixture.questions.find((q) => q.id === action.questionId);
        if (question !== undefined) onAnswer({ kind: 'question', question });
        return;
      }
      case 'human-task':
        onOpenAudit(action.tab, action.anchor);
        return;
      // Krok PIL1 (vada V6): oblast proverení vede do teze sekce auditu,
      // kam vede jeji akce „Zadat" v seznamu oblastí.
      case 'open-audit':
        onOpenAudit(action.tab, action.anchor);
        return;
      case 'confirm-kind':
        confirmKind();
        return;
    }
  };

  return (
    <div data-testid="case-cockpit">
      {/* ---------------------------------------------- 2. Co musím udělat? */}
      <Question
        heading={t('webAdmin.caseCockpit.tasks.heading', cs)}
        badge={
          tasks.length === 0
            ? t('webAdmin.caseCockpit.tasks.none', cs)
            : doneCount === tasks.length
              ? t('webAdmin.caseCockpit.tasks.allDone', cs)
              : t('webAdmin.caseCockpit.tasks.progress', {
                  ...cs,
                  values: { done: String(doneCount), total: String(tasks.length) },
                })
        }
        badgeClass={tasks.length > 0 && doneCount === tasks.length ? 'rh-a2-badge--ok' : ''}
        hint={t('webAdmin.caseCockpit.tasks.hint', cs)}
        testId="case-cockpit-tasks"
      >
        {tasks.length > 0 ? (
          <>
            <div className="rh-a2-bar" data-testid="case-cockpit-tasks-progress" data-pct={String(Math.round((doneCount / tasks.length) * 100))}>
              <i style={{ width: `${String(Math.round((doneCount / tasks.length) * 100))}%` }} />
            </div>
            <ul className="rh-a2-rows" data-testid="case-cockpit-task-list">
              {tasks.map((task) => (
                <li
                  key={task.id}
                  className={task.done ? 'rh-a2-rows__done' : undefined}
                  data-testid={task.testId}
                  data-done={String(task.done)}
                >
                  <span
                    className={`rh-a2-dot ${task.done ? 'rh-a2-dot--ok' : IMPACT_DOT_CLASS[task.impact]}`}
                    aria-hidden="true"
                  />
                  <span className="rh-a2-grow">
                    <span className="rh-a2-title">{task.text}</span>
                  </span>
                  {task.done ? (
                    <span className="rh-a2-badge rh-a2-badge--ok">{t('webAdmin.caseCockpit.tasks.doneBadge', cs)}</span>
                  ) : (
                    <>
                      <span className={`rh-a2-badge ${IMPACT_BADGE_CLASS[task.impact]}`}>
                        {IMPACT_CAPTIONS[task.impact]}
                      </span>
                      {task.action.kind === 'link' ? (
                        <Link to={task.action.to} className="rh-a2-btn rh-a2-btn--sm" data-testid={`${task.testId}-button`}>
                          {t('webAdmin.caseCockpit.tasks.openQueue', cs)}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="rh-a2-btn rh-a2-btn--sm"
                          disabled={busy}
                          onClick={() => { openTask(task); }}
                          data-testid={`${task.testId}-button`}
                        >
                          {task.action.kind === 'human-task'
                            ? t('webAdmin.caseCockpit.tasks.openTask', cs)
                            : task.action.kind === 'open-audit'
                              ? t('webAdmin.caseCockpit.tasks.openArea', cs)
                              : task.action.kind === 'confirm-kind'
                                ? t('webAdmin.caseCockpit.tasks.confirm', cs)
                                : t('webAdmin.caseCockpit.tasks.answer', cs)}
                        </button>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {error !== null ? (
          <p role="alert" className="rh-a2-error" style={{ marginTop: 10 }} data-testid="case-cockpit-error">
            {error}
          </p>
        ) : null}
        {notice !== null ? (
          <p role="status" className="rh-a2-meta" style={{ marginTop: 10 }} data-testid="case-cockpit-notice">
            {notice}
          </p>
        ) : null}
      </Question>

    </div>
  );
}

/*
 * Krok UXP4b bod 1 (nalez P2-5): karta „Co zbývá? N z 10" ZRUSENA. Cesta
 * zakazky je od tohoto kroku ROZBALENI jedineho ukazatele postupu v karte
 * nahore (`CaseJourneyDetails` v `components/case-detail-v4.tsx`) — dve ruzna
 * cisla o tomtez na jedne obrazovce byl presne nalez P2-5. Vypocet
 * (`lib/case-journey.ts`) ani katalog textu se nemeni.
 */
